import { Key, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionItem, Chip, Spinner } from '@heroui/react';
import { Order } from 'src/utils/interfaces/interfaces';
import { FaChevronDown, FaChevronUp } from 'src/icons';
import { useLazyGetPaymentUrlQuery } from 'src/services/payment/orderPayment';
import { formatText } from 'src/utils/stringUtils';

interface OrdersAccordionViewProps {
  type: 'other' | 'ongoing' | 'completed' | 'approved' | 'ready' | 'pending' | 'draft';
  orders: Order[];
  isLoading?: boolean;
  onOrderClick?: (order: Order) => void;
  activeOrder?: string | null;
}

const HEADER_GRID =
  'grid min-w-0 grid-cols-[56px_minmax(200px,1fr)_120px_minmax(200px,1.15fr)_minmax(360px,1.9fr)_minmax(360px,1.9fr)_minmax(120px,0.65fr)] items-center gap-4';

export default function OrdersAccordionView({
  type,
  orders,
  isLoading = false,
  onOrderClick,
  activeOrder = null,
}: OrdersAccordionViewProps) {
  const { t } = useTranslation();
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const approvedOrders = useMemo(
    () => orders.filter((o) => o.status === 'approved'),
    [orders]
  );

  const nonApprovedOrders = useMemo(
    () => orders.filter((o) => o.status !== 'approved'),
    [orders]
  );

  const groups = useMemo(() => {
    return nonApprovedOrders.reduce<Record<string, Order[]>>((acc, o) => {
      const base = o.orderNumber?.split('-')[0] ?? t('orders_page.unknown');
      acc[base] = acc[base] || [];
      acc[base].push(o);
      return acc;
    }, {});
  }, [nonApprovedOrders, t]);

  const entries = useMemo(
    () =>
      Object.entries(groups).sort(([, g1], [, g2]) => {
        const n1 = Math.max(...g1.map((o) => new Date(o.createdAt).getTime()));
        const n2 = Math.max(...g2.map((o) => new Date(o.createdAt).getTime()));
        return n2 - n1;
      }),
    [groups]
  );

  useEffect(() => {
    if (!activeOrder) return;

    const matchingEntry = entries.find(([, groupOrders]) =>
      groupOrders.some((order) => order._id === activeOrder)
    );

    if (matchingEntry) {
      setExpandedKey(matchingEntry[0]);
    }
  }, [activeOrder, entries]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner />
      </div>
    );
  }

  if (!orders.length) {
    return (
      <div className="p-4" style={{ color: 'var(--text-main)' }}>
        {t('orders_page.no_orders')}
      </div>
    );
  }

  return (
    <div
      className="orders-scroll max-h-[63vh] overflow-x-auto overflow-y-auto rounded-lg border shadow-card"
      style={{
        background: 'var(--bg-surface)',
        borderColor: 'var(--border-light)',
      }}
    >
      <div
        className="sticky top-0 z-20 px-4 py-3"
        style={{
          backgroundColor: 'var(--bg-surface-2)',
          borderBottom: '1px solid var(--border-light)',
        }}
      >
        <div className={HEADER_GRID}>
          <div />
          <HeaderCell>{t('orders_page.order_number')}</HeaderCell>
          <HeaderCell>{t('orders_page.status')}</HeaderCell>
          <HeaderCell>{t('orders_page.event_date')}</HeaderCell>
          <HeaderCell>{t('orders_page.origin')}</HeaderCell>
          <HeaderCell>{t('orders_page.destination')}</HeaderCell>
          <div />
        </div>
      </div>

      <div>
        {approvedOrders.map((order) => (
          <StaticOrderRow
            key={order._id}
            base={order.orderNumber || '—'}
            order={order}
            onOrderClick={onOrderClick}
            isActive={order._id === activeOrder}
          />
        ))}

        {entries.map(([base, groupOrders]) => {
          const first = groupOrders[0];
          const hasActiveSelection = groupOrders.some(
            (order) => order._id === activeOrder
          );
          const isExpandable =
            first.status !== 'pending_approval' && first.status !== 'approved';

          if (!isExpandable) {
            return (
              <StaticOrderRow
                key={base}
                base={base}
                order={first}
                onOrderClick={onOrderClick}
                isActive={hasActiveSelection}
              />
            );
          }

          return (
            <AccordionGroupRow
              key={base}
              base={base}
              orders={groupOrders}
              isOpen={expandedKey === base}
              onOpenChange={(isOpen) => setExpandedKey(isOpen ? base : null)}
              onOrderClick={onOrderClick}
              type={type}
              activeOrder={activeOrder}
              hasActiveSelection={hasActiveSelection}
            />
          );
        })}
      </div>
    </div>
  );
}

function HeaderCell({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-left font-bold" style={{ color: 'var(--text-muted)' }}>
      {children}
    </div>
  );
}

interface StaticOrderRowProps {
  base: string;
  order: Order;
  onOrderClick?: (order: Order) => void;
  isActive?: boolean;
}

function StaticOrderRow({
  base,
  order,
  onOrderClick,
  isActive = false,
}: StaticOrderRowProps) {
  return (
    <div
      onClick={() => onOrderClick?.(order)}
      className="cursor-pointer px-4 py-3"
      style={{
        borderBottom: '1px solid var(--border-light)',
        textTransform: 'capitalize',
        backgroundColor: isActive ? 'var(--color-primary-light)' : 'transparent',
      }}
    >
      <div className={HEADER_GRID}>
        <div className="h-10" />
        <SummaryCells base={base} order={order} />
      </div>
    </div>
  );
}

interface AccordionGroupRowProps {
  base: string;
  orders: Order[];
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onOrderClick?: (order: Order) => void;
  type: OrdersAccordionViewProps['type'];
  activeOrder?: string | null;
  hasActiveSelection?: boolean;
}

function AccordionGroupRow({
  base,
  orders,
  isOpen,
  onOpenChange,
  onOrderClick,
  activeOrder = null,
  hasActiveSelection = false,
}: AccordionGroupRowProps) {
  const first = orders[0];
  const [isPaying, setIsPaying] = useState(false);
  const [getPaymentUrl] = useLazyGetPaymentUrlQuery();

  const handleSelectionChange = (keys: 'all' | Set<Key>) => {
    if (keys === 'all') {
      onOpenChange(true);
      return;
    }

    onOpenChange(keys.size > 0);
  };

  const handlePayClick = async () => {
    setIsPaying(true);
    try {
      const res = await getPaymentUrl(first._id).unwrap();
      if (res?.url) {
        window.location.href = res.url;
      } else {
        console.error('Payment URL not found');
      }
    } catch (error) {
      console.error('Failed to fetch payment URL', error);
    } finally {
      setIsPaying(false);
    }
  };

  return (
    <Accordion
      selectionMode="single"
      selectedKeys={isOpen ? [base] : []}
      onSelectionChange={handleSelectionChange}
      showDivider={false}
      className="px-0"
      itemClasses={{
        base: 'rounded-none border-b border-[var(--border-light)] data-[open=true]:shadow-[inset_4px_0_0_0_var(--color-primary)]',
        heading: 'w-full',
        trigger: 'w-full px-4 py-3 data-[hover=true]:bg-transparent',
        titleWrapper: 'min-w-0',
        title: 'min-w-0',
        content: 'p-0',
      }}
    >
      <AccordionItem
        key={base}
        aria-label={base}
        hideIndicator
        title={
          <div
            className={HEADER_GRID}
            style={{
              backgroundColor: hasActiveSelection
                ? 'var(--color-primary-light)'
                : 'transparent',
            }}
          >
            <div className="flex h-10 w-10 items-center justify-center text-[var(--text-main)]">
              {isOpen ? <FaChevronUp /> : <FaChevronDown />}
            </div>
            <SummaryCells
              base={base}
              order={first}
              compact
              actionContent={
                first.status === 'pending_payment' ? (
                  <PaymentAction
                    isPaying={isPaying}
                    paymentStatus={first.paymentStatus}
                    onPay={handlePayClick}
                  />
                ) : null
              }
            />
          </div>
        }
      >
        <ExpandedOrders
          orders={orders}
          onOrderClick={onOrderClick}
          activeOrder={activeOrder}
        />
      </AccordionItem>
    </Accordion>
  );
}

function PaymentAction({
  isPaying,
  paymentStatus,
  onPay,
}: {
  isPaying: boolean;
  paymentStatus?: string;
  onPay: () => Promise<void>;
}) {
  if (paymentStatus === 'processing') {
    return (
      <div
        className="ml-10 flex min-w-28 items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm"
        style={{
          borderColor: 'var(--border-light)',
          color: 'var(--text-main)',
          backgroundColor: 'var(--bg-surface)',
        }}
      >
        <span>Processing</span>
        <Spinner size="sm" variant="dots" color="warning" />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        void onPay();
      }}
      disabled={isPaying}
      className="ml-10 inline-flex min-w-28 items-center justify-center rounded-lg px-3 py-2 text-sm font-medium transition-opacity disabled:cursor-not-allowed disabled:opacity-70"
      style={{
        backgroundColor: 'var(--color-primary)',
        color: 'var(--text-invert)',
      }}
    >
      {isPaying ? <Spinner size="sm" color="white" /> : 'Proceed to Pay'}
    </button>
  );
}

function SummaryCells({
  base,
  order,
  compact = false,
  actionContent = null,
}: {
  base: string;
  order: Order;
  compact?: boolean;
  actionContent?: React.ReactNode;
}) {
  return (
    <>
      <SummaryText compact={compact}>{base}</SummaryText>
      <SummaryText compact={compact}>{formatText(order.status) ?? '—'}</SummaryText>
      <SummaryText compact={compact}>
        {order.eventDate
          ? moment(order.eventDate).format('Do MMM YYYY, h:mm A')
          : 'N/A'}
      </SummaryText>
      <SummaryText compact={compact} truncate title={order.address?.origin}>
        {order.address?.origin ?? '—'}
      </SummaryText>
      <SummaryText compact={compact} truncate title={order.address?.destination}>
        {order.address?.destination ?? '—'}
      </SummaryText>
      <div className="min-h-10">{actionContent}</div>
    </>
  );
}

function SummaryText({
  children,
  compact = false,
  truncate = false,
  title,
}: {
  children: React.ReactNode;
  compact?: boolean;
  truncate?: boolean;
  title?: string;
}) {
  return (
    <div
      className={`${truncate ? 'overflow-hidden text-ellipsis whitespace-nowrap' : ''} ${compact ? 'text-[15px]' : 'text-base'
        }`}
      style={{ color: 'var(--text-main)' }}
      title={title}
    >
      {children}
    </div>
  );
}

function ExpandedOrders({
  orders,
  onOrderClick,
  activeOrder = null,
}: {
  orders: Order[];
  onOrderClick?: (order: Order) => void;
  activeOrder?: string | null;
}) {
  const { t } = useTranslation();

  return (
    <div
      className="border-t p-4"
      style={{
        backgroundColor: 'var(--bg-surface)',
        borderColor: 'var(--border-light)',
        boxShadow: 'inset 4px 0 0 0 var(--color-primary)',
      }}
    >
      <div className="relative ml-6 flex flex-col gap-3">
        <div className="absolute bottom-[24px] left-[8px] top-[24px] z-0 w-[2px] rounded-full bg-[var(--border-light)]" />

        {orders.map((order) => (
          <div
            key={order._id}
            onClick={() => onOrderClick?.(order)}
            className="relative z-10 grid w-full cursor-pointer grid-cols-5 items-center gap-6 rounded-lg border bg-[var(--bg-surface)] p-4 shadow-sm transition-all hover:scale-[1.01] hover:shadow-md"
            style={{
              borderColor:
                order._id === activeOrder
                  ? 'color-mix(in srgb, var(--color-primary) 40%, var(--border-light))'
                  : 'var(--border-light)',
              backgroundColor:
                order._id === activeOrder
                  ? 'var(--color-primary-light)'
                  : 'var(--bg-surface)',
              boxShadow:
                order._id === activeOrder
                  ? 'inset 4px 0 0 0 var(--color-primary)'
                  : undefined,
            }}
          >
            <div className="flex flex-col">
              <span className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
                {t('orders_page.order_number', 'Order Number')}
              </span>
              <span className="text-sm font-medium text-[var(--text-main)]">
                {order.orderNumber}
              </span>
            </div>

            <div className="flex flex-col">
              <span className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
                {t('orders_page.order_type', 'Order Type')}
              </span>
              <div>
                <Chip
                  size="sm"
                  variant="flat"
                  className="h-6 bg-[var(--bg-surface-2)] px-2 text-[11px] capitalize text-[var(--text-main)]"
                >
                  {order.orderType || 'N/A'}
                </Chip>
              </div>
            </div>

            <div className="flex flex-col">
              <span className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
                {t('orders_page.status', 'Status')}
              </span>
              <span className="text-sm capitalize text-[var(--text-main)]">
                {formatText(order.status)}
              </span>
            </div>

            <div className="flex flex-col">
              <span className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
                {t('orders_page.valid_from', 'Valid From')}
              </span>
              <span className="text-sm text-[var(--text-main)]">
                {order.validFrom
                  ? moment(order.validFrom).format('Do MMM YYYY, h:mm A')
                  : 'N/A'}
              </span>
            </div>

            <div className="flex flex-col">
              <span className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
                {t('orders_page.valid_to', 'Valid To')}
              </span>
              <span className="text-sm text-[var(--text-main)]">
                {order.validTo
                  ? moment(order.validTo).format('Do MMM YYYY, h:mm A')
                  : 'N/A'}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
